// privateProfileService.ts
// Service for managing user private profiles with Zod validation
// Handles CRUD operations, progress tracking, and user context management
//
// ARCHITECTURE: Uses setDoc for profile creation, updateDoc for all updates
// This ensures optimal performance and proper Firebase security rule validation
//
// IMPORTANT: For profile creation troubleshooting, see docs/PROFILE_CREATION_GUIDE.md

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import { auth, db, doc, getDoc, updateDoc, setDoc } from '../config/firebase';
import FirestoreService from './firestore';
import { cleanUndefinedDeep } from '../utils/cleanUndefined';
import { getDeviceInfo, getComprehensiveDeviceInfo } from '../utils/deviceInfo';
import {
  PrivateProfile,
  PrivateProfileSchema,
  PrivateProfileCreationData,
  PrivateProfileCreationDataSchema,
  PrivateProfileUpdate,
  UserBookProgress,
  UserInsightProgress,
  OnboardingAnswersCollection,
  ActiveContext,
  CoreProfile,
  createEmptyPrivateProfile,
  updatePrivateProfileTimestamp,
  addBookProgress,
  updateBookProgress,
  updateActiveContext,
  updateCoreProfile,
  updateOnboardingAnswers,
  ProfileSummary,
  DailyRecommendations,
  AIInsightRecommendation,
  MentorChatHistory,
  AIBookRecommendation,
  updateProfileSummary,
  updateDailyRecommendations,
  addChatHistory,
} from '../models/PrivateProfile';

// Normalize book IDs to base integer string (strip language suffix, validate digits)
const normalizeBookId = (bookId: string | number | undefined | null): string | null => {
  if (bookId === undefined || bookId === null) return null;
  const raw = String(bookId);
  const base = raw.includes('_') ? raw.split('_')[0] : raw;
  return /^\d+$/.test(base) ? base : null;
};

// Cache configuration
const PROFILE_CACHE_PREFIX = 'profile_';
const PROFILE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export class PrivateProfileService {
  private static readonly COLLECTION_NAME = 'privateProfile';
  private static migratedUsers = new Set<string>(); // Track migrated users in session
  private static memoryCache = new Map<string, { data: PrivateProfile; timestamp: number }>(); // Memory cache

  private static isNotFoundError(error: any): boolean {
    const code = error?.code ?? error?.message;
    return typeof code === 'string' && code.includes('not-found');
  }

  private static isPermissionDeniedError(error: any): boolean {
    const code = error?.code ?? error?.message;
    return typeof code === 'string' && code.includes('permission-denied');
  }

  /**
   * Helper to update Firestore document (handles web vs native)
   */
  private static async updateDoc(userId: string, data: any): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      await updateDoc(docRef, data);
    } catch (error: any) {
      // If the doc doesn't exist yet, create it and retry once.
      if (this.isNotFoundError(error)) {
        console.warn('[PrivateProfileService] Update failed with not-found; creating minimal profile and retrying:', userId);
        const created = await this.ensureProfileExists(userId, {
          email: auth.currentUser?.email || undefined,
          displayName: auth.currentUser?.displayName || undefined,
        });
        if (!created.success) {
          throw error;
        }

        const docRef = doc(db, this.COLLECTION_NAME, userId);
        await updateDoc(docRef, data);
      } else if (this.isPermissionDeniedError(error)) {
        // Some users may have older/invalid profile docs that fail strict rules on any update.
        // Recovery: merge-write a full minimal profile (fills required fields) then retry the update once.
        console.warn('[PrivateProfileService] Update failed with permission-denied; attempting profile repair and retry:', userId);
        try {
          const repairProfile = this.createMinimalProfile(userId, {
            email: auth.currentUser?.email || undefined,
            displayName: auth.currentUser?.displayName || undefined,
          });

          const docRef = doc(db, this.COLLECTION_NAME, userId);
          await setDoc(docRef, repairProfile, { merge: true });

          // Retry the original update
          await updateDoc(docRef, data);
        } catch (repairError) {
          console.error('[PrivateProfileService] Profile repair failed:', repairError);
          throw error;
        }
      } else {
        throw error;
      }
    }
    
    // Invalidate cache after successful update
    await this.invalidateProfileCache(userId);
  }

  /**
   * Ensure a user's profile exists, creating a minimal one if needed
   * This should be called immediately after authentication
   */
  static async ensureProfileExists(
    userId: string,
    authData?: {
      email?: string;
      displayName?: string;
    }
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      // Fast path: if we already have a valid profile cached, return it.
      // Note: this does NOT guarantee Firestore has the doc; we rely on `initializeUserProfile()` to create it once after auth.
      const existing = await this.getProfile(userId);
      if (existing.success && existing.data) {
        console.log('✅ Profile already exists for user:', userId);
        return existing;
      }

            console.log('📝 Creating minimal profile for user:', userId);
      
      // Create minimal profile that matches working Firestore profiles
      const completeProfile = this.createMinimalProfile(userId, authData);
    
      // Save complete profile to Firestore
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      try {
        await setDoc(docRef, completeProfile);
        console.log('✅ Profile setDoc completed for user:', userId);
      } catch (setDocError: any) {
        console.error('❌ setDoc failed for profile creation:', setDocError);
        
        // Report critical profile creation failure to Sentry
        const { errorReporting } = require('./errorReportingService');
        errorReporting.reportError(setDocError, {
          userId,
          screen: 'PrivateProfileService',
          action: 'ensureProfileExists_setDoc',
          metadata: {
            errorCode: setDocError?.code,
            errorMessage: setDocError?.message,
            isAnonymous: auth.currentUser?.isAnonymous,
            userAuthenticated: !!auth.currentUser,
            isCritical: true,
          },
        });
        
        throw setDocError; // Re-throw so caller knows it failed
      }
      
      // Verify the profile was actually created by reading it back
      const verification = await getDoc(docRef);
      if (!verification.exists()) {
        const error = new Error('Profile creation succeeded but document not found on verification');
        console.error('❌ Profile verification failed:', error);
        
        const { errorReporting } = require('./errorReportingService');
        errorReporting.reportError(error, {
          userId,
          screen: 'PrivateProfileService',
          action: 'ensureProfileExists_verification',
          metadata: {
            setDocCompleted: true,
            isAnonymous: auth.currentUser?.isAnonymous,
            isCritical: true,
          },
        });
        
        return {
          success: false,
          error: 'Profile creation succeeded but verification failed',
        };
      }
      
      console.log('✅ Profile created and verified for user:', userId);
      
      // Invalidate cache to ensure fresh read next time
      await this.invalidateProfileCache(userId);
      
      return { success: true, data: completeProfile };
    } catch (error: any) {
      console.error('❌ Error ensuring profile exists:', error);
      return {
        success: false,
        error: error.message || 'Failed to ensure profile exists',
      };
    }
  }

  /**
   * Create a minimal profile that matches working profiles exactly
   * Based on real working profile structure from Firestore
   */
  private static createMinimalProfile(
    userId: string,
    authData?: {
      email?: string;
      displayName?: string;
    }
  ): PrivateProfile {
    const now = Date.now() / 1000; // Float timestamp as required by rules
    
    // Get actual device information
    const deviceData = getDeviceInfo();
    
    // Build minimal profile matching working profile structure exactly
    const profile: any = {
      id: userId,
      
      // === CORE REQUIRED FIELDS (actual device data) ===
      appVersion: deviceData.appVersion,
      createdAtTimestamp: now,
      updatedAtTimestamp: now,
      deviceInfo: deviceData.deviceInfo,
      platform: deviceData.platform,
      startingAppVersion: deviceData.startingAppVersion,
      storeId: deviceData.storeId,
      timezone: deviceData.timezone,
      isTester: false,
      isEmailVerified: false,
      
      // Subscription fields (RevenueCat source of truth)
      subscriptionTier: 'free' as const, // Default to free tier
      subscriptionExpiresAt: null,
      lastRevenueCatSync: now,
      
      // === MINIMAL REQUIRED COLLECTIONS (from working profile) ===
      userData: {}, // Required map - can be empty initially
      
      // === WORKING PROFILE HAS INVITATION KEY AS STRING ===
      invitationKey: 'web-001', // String value, not null/undefined
      
      // === REQUIRED BY OUR APP ARCHITECTURE ===
      // Only add the absolutely necessary fields for our app to work
      dailyActivity: [], // Activity tracking
      booksProgress: {}, // Book progress tracking
      insightsProgress: {}, // Insight progress tracking
      onboardingAnswers: {
        sessionId: '',
        completedAt: 0,
        version: '1.0',
        answers: [],
      },
      activeContext: {
        lastUpdated: now,
        source: 'creation' as const,
        information: 'Profile created',
      },
      
      // === DEPRECATED FIELDS (minimal set for compatibility) ===
      books: {}, // Legacy field
      chapters: {}, // Legacy field
      habits: {}, // Legacy field
      quotes: {}, // Legacy field
      points: {}, // Legacy field
      futureBooksVoted: [], // Legacy field
      chosenCategories: [], // Legacy field
      habitCategories: [], // Legacy field
      totalPoints: 0, // Legacy field
    };

    // Add optional fields if provided (only include if they have actual values)
    if (authData?.email) {
      profile.email = authData.email;
    }
    if (authData?.displayName) {
      profile.displayName = authData.displayName;
    }

    return PrivateProfileSchema.parse(profile);
  }

  /**
   * Create a user's private profile (legacy method - use ensureProfileExists instead)
   * @deprecated Use ensureProfileExists for better reliability
   */
  static async createProfile(
    userId: string,
    creationData: Partial<PrivateProfileCreationData> = {}
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    return this.ensureProfileExists(userId, {
      email: creationData.email,
      displayName: creationData.displayName,
    });
  }

  /**
   * Get a user's private profile
   * Checks memory cache -> AsyncStorage cache -> Firestore
   */
  static async getProfile(
    userId: string
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      // Check memory cache first (fastest)
      const memCached = this.getFromMemoryCache(userId);
      if (memCached) {
        // console.log(`[ProfileCache] Hit memory cache for user: ${userId}`);
        return { success: true, data: memCached };
      }

      // Check AsyncStorage cache (fast)
      const asyncCached = await this.getFromCache(userId);
      if (asyncCached) {
        console.log(`[ProfileCache] Hit AsyncStorage cache for user: ${userId}`);
        // Update memory cache
        this.setMemoryCache(userId, asyncCached);
        return { success: true, data: asyncCached };
      }

      // Fetch from Firestore (slow)
      console.log(`[ProfileCache] Cache miss, fetching from Firestore for user: ${userId}`);
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return { success: false, error: 'Profile not found' };
      }

      const rawData = docSnap.data();
      
      try {
        // Try to validate with current schema
        const profile = PrivateProfileSchema.parse(rawData);
        
        // Update both caches
        this.setMemoryCache(userId, profile);
        await this.setCache(userId, profile);
        
        return { success: true, data: profile };
      } catch (validationError) {
        // Schema mismatch - fill in missing fields with defaults
        if (!this.migratedUsers.has(userId)) {
          console.log(`🔄 Migrating profile schema for user ${userId}...`);
          this.migratedUsers.add(userId);
        }
        
        const now = Date.now() / 1000;
        
        // Create a profile with missing fields filled in
        const profileWithDefaults = {
          ...rawData,
          // The id is just the userId (document ID)
          id: userId,
          // Ensure timestamps are present (required fields)
          createdAtTimestamp: rawData.createdAtTimestamp || now,
          updatedAtTimestamp: rawData.updatedAtTimestamp || now,
          // Fill in missing activeContext or merge missing fields
          activeContext: {
            lastUpdated: now,
            source: 'profile_update' as const,
            ...rawData.activeContext, // Keep existing fields, override missing ones
          },
          // Fill in missing coreProfile or merge missing fields
          coreProfile: {
            lastUpdated: now,
            source: 'creation',
            ...rawData.coreProfile, // Keep existing fields, override missing ones
          },
          // Fix profileSummary if it exists but is missing required fields
          ...(rawData.profileSummary && {
            profileSummary: {
              lastUpdated: now,
              ...rawData.profileSummary, // Keep existing fields, add missing ones
            },
          }),
          // Ensure other required fields have defaults
          dailyActivity: rawData.dailyActivity || [],
          platform: rawData.platform || 'React Native',
          subscriptionTier: rawData.subscriptionTier || 'free',
          subscriptionExpiresAt: rawData.subscriptionExpiresAt ?? null,
          lastRevenueCatSync: rawData.lastRevenueCatSync || now,
          isTester: rawData.isTester ?? false,
          booksProgress: rawData.booksProgress || {},
          insightsProgress: rawData.insightsProgress || {},
          onboardingAnswers: rawData.onboardingAnswers || {
            sessionId: '',
            completedAt: 0,
            version: '1.0',
            answers: [],
          },
          userData: rawData.userData || {},
          // Handle deprecated fields
          books: rawData.books || {},
          chapters: rawData.chapters || {},
          habits: rawData.habits || {},
          quotes: rawData.quotes || {},
          futureBooksVoted: rawData.futureBooksVoted || [],
          chosenCategories: rawData.chosenCategories || [],
          habitCategories: rawData.habitCategories || [],
          totalPoints: rawData.totalPoints || 0,
          points: rawData.points || {},
          recommendationHistory: rawData.recommendationHistory || [],
        };
        
        // Now validate the complete profile
        const profile = PrivateProfileSchema.parse(profileWithDefaults);
        
        // Only log completion once per user per session
        if (this.migratedUsers.size === 1 && this.migratedUsers.has(userId)) {
          console.log(`✅ Schema migration completed`);
        }
        
        // Update both caches
        this.setMemoryCache(userId, profile);
        await this.setCache(userId, profile);
        
        return { success: true, data: profile };
      }
    } catch (error: any) {
      console.error('Error getting private profile:', error);
      return {
        success: false,
        error: error.message || 'Failed to get private profile',
      };
    }
  }

  /**
   * Update a user's private profile (simple fields only)
   * Uses partial updates for better performance and security
   */
  static async updateProfile(
    userId: string,
    updates: PrivateProfileUpdate
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      // DEBUG: Check authentication state
      const currentUser = auth.currentUser;
      console.log('[ProfileService] updateProfile called:', {
        requestedUserId: userId,
        currentUserId: currentUser?.uid,
        isAuthenticated: !!currentUser,
        isAnonymous: currentUser?.isAnonymous,
        idsMatch: currentUser?.uid === userId,
      });

      if (!currentUser) {
        return { success: false, error: 'No user authenticated' };
      }

      if (currentUser.uid !== userId) {
        return { success: false, error: `Auth mismatch: trying to update ${userId} but authenticated as ${currentUser.uid}` };
      }

      // Get current profile for validation
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error };
      }

      // Create update object with only changed fields + timestamp
      const updateData = {
        ...updates,
        updatedAtTimestamp: Date.now() / 1000, // Always update timestamp
      };

      // Clean undefined values
      const cleanedUpdates = cleanUndefinedDeep(updateData);

      // Use updateDoc to only update specific fields instead of entire document
      await this.updateDoc(userId, cleanedUpdates);

      // Return updated profile data (merge updates with current data)
      const updatedProfile = updatePrivateProfileTimestamp({
        ...currentResult.data,
        ...updates,
      });
      
      // Cache is automatically invalidated by updateDoc helper
      
      return { success: true, data: updatedProfile };
    } catch (error: any) {
      if (__DEV__) {
        console.warn('Error updating private profile:', error);
      }
      return {
        success: false,
        error: error.message || 'Failed to update private profile',
      };
    }
  }

  /**
   * Add or update book progress for a user
   * Uses partial updates for better performance and security
   */
  static async updateUserBookProgress(
    userId: string,
    bookId: string,
    progressUpdates: Partial<UserBookProgress>
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error };
      }

      let updatedProfile = currentResult.data;

      // Add book progress if it doesn't exist
      if (!updatedProfile.booksProgress[bookId]) {
        updatedProfile = addBookProgress(updatedProfile, bookId);
      }

      // Update with progress data
      updatedProfile = updateBookProgress(updatedProfile, bookId, progressUpdates);

      // Use updateDoc to only update the booksProgress field
      const cleanedBooksProgress = cleanUndefinedDeep(updatedProfile.booksProgress);
      
      await this.updateDoc(userId, {
        booksProgress: cleanedBooksProgress,
        updatedAtTimestamp: Date.now() / 1000,
      });
      
      return { success: true, data: updatedProfile };
    } catch (error: any) {
      console.error('Error updating book progress:', error);
      return {
        success: false,
        error: error.message || 'Failed to update book progress',
      };
    }
  }

  /**
   * Add or update insight progress for a user
   * Uses partial updates for better performance and security
   */
  static async updateUserInsightProgress(
    userId: string,
    insightId: string,
    bookId: string | undefined,
    progressUpdates: Partial<UserInsightProgress>
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error };
      }

      const now = Date.now() / 1000;
      const existingProgress = currentResult.data.insightsProgress[insightId];

      // Create or update insight progress
      const resolvedBookId = bookId || existingProgress?.bookId || progressUpdates.bookId || 'unknown';

      const insightProgress: UserInsightProgress = {
        id: insightId,
        bookId: resolvedBookId,
        startedAt: existingProgress?.startedAt || now,
        lastAccessedAt: now,
        isCompleted: false,
        ...progressUpdates,
      };

      const updatedProfile = updatePrivateProfileTimestamp({
        ...currentResult.data,
        insightsProgress: {
          ...currentResult.data.insightsProgress,
          [insightId]: insightProgress,
        },
      });

      // Use updateDoc to only update the insightsProgress field
      const cleanedInsightsProgress = cleanUndefinedDeep(updatedProfile.insightsProgress);
      
      await this.updateDoc(userId, {
        insightsProgress: cleanedInsightsProgress,
        updatedAtTimestamp: now,
      });
      
      return { success: true, data: updatedProfile };
    } catch (error: any) {
      console.error('Error updating insight progress:', error);
      return {
        success: false,
        error: error.message || 'Failed to update insight progress',
      };
    }
  }

  /**
   * Toggle saved status for an insight
   * @param userId - User's Firebase UID
   * @param insightId - Insight ID
   * @param bookId - Parent book ID
   * @returns Result with updated profile
   */
  static async toggleInsightSaved(
    userId: string,
    insightId: string,
    bookId: string
  ): Promise<{ success: boolean; isSaved: boolean; data?: PrivateProfile; error?: string }> {
    try {
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, isSaved: false, error: currentResult.error };
      }

      const now = Date.now() / 1000;
      const existingProgress = currentResult.data.insightsProgress[insightId];
      
      // Toggle saved status
      const newIsSaved = !existingProgress?.isSaved;
      
      // Create or update insight progress with saved status
      const insightProgress: UserInsightProgress = {
        id: insightId,
        bookId: bookId,
        startedAt: existingProgress?.startedAt || now,
        lastAccessedAt: now,
        isCompleted: existingProgress?.isCompleted || false,
        completedAt: existingProgress?.completedAt,
        isSaved: newIsSaved,
        savedAt: newIsSaved ? now : undefined,
        recommendationReason: existingProgress?.recommendationReason,
        recommendedAt: existingProgress?.recommendedAt,
      };

      const updatedProfile = updatePrivateProfileTimestamp({
        ...currentResult.data,
        insightsProgress: {
          ...currentResult.data.insightsProgress,
          [insightId]: insightProgress,
        },
      });

      // Use updateDoc to only update the insightsProgress field
      const cleanedInsightsProgress = cleanUndefinedDeep(updatedProfile.insightsProgress);
      
      await this.updateDoc(userId, {
        insightsProgress: cleanedInsightsProgress,
        updatedAtTimestamp: now,
      });
      
      console.log(`💾 Insight ${newIsSaved ? 'saved' : 'unsaved'}: ${insightId}`);
      
      return { success: true, isSaved: newIsSaved, data: updatedProfile };
    } catch (error: any) {
      console.error('Error toggling insight saved status:', error);
      return {
        success: false,
        isSaved: false,
        error: error.message || 'Failed to toggle insight saved status',
      };
    }
  }

  /**
   * Mark an insight as shown today (for "Insight of the Day" rotation)
   * @param userId - User's Firebase UID
   * @param insightId - Insight ID
   * @param bookId - Parent book ID
   * @returns Result with updated profile
   */
  static async markInsightAsShownToday(
    userId: string,
    insightId: string,
    bookId: string
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error };
      }

      const now = Date.now() / 1000;
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const existingProgress = currentResult.data.insightsProgress[insightId];
      const resolvedBookId = bookId || existingProgress?.bookId || 'unknown';

      // Create or update insight progress with today's date
      const insightProgress: UserInsightProgress = {
        id: insightId,
        bookId: resolvedBookId,
        startedAt: existingProgress?.startedAt || now,
        lastAccessedAt: now,
        isCompleted: existingProgress?.isCompleted || false,
        completedAt: existingProgress?.completedAt,
        isSaved: existingProgress?.isSaved,
        savedAt: existingProgress?.savedAt,
        recommendationReason: existingProgress?.recommendationReason,
        recommendedAt: existingProgress?.recommendedAt,
        lastShownAsInsightOfDayDate: today, // Mark as shown today
      };

      const updatedProfile = updatePrivateProfileTimestamp({
        ...currentResult.data,
        insightsProgress: {
          ...currentResult.data.insightsProgress,
          [insightId]: insightProgress,
        },
      });

      // Use updateDoc to only update the insightsProgress field
      const cleanedInsightsProgress = cleanUndefinedDeep(updatedProfile.insightsProgress);
      
      await this.updateDoc(userId, {
        insightsProgress: cleanedInsightsProgress,
        updatedAtTimestamp: now,
      });
      
      console.log(`✅ Marked insight ${insightId} as shown on ${today}`);
      
      return { success: true, data: updatedProfile };
    } catch (error: any) {
      console.error('Error marking insight as shown today:', error);
      return {
        success: false,
        error: error.message || 'Failed to mark insight as shown today',
      };
    }
  }

  /**
   * Append new insights to the current recommendation plan without
   * pushing a new recommendationHistory entry.
   */
  static async appendInsightsToCurrentPlan(
    userId: string,
    newInsights: AIInsightRecommendation[],
    fallbackTitle: string = 'Your Daily Steps'
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error || 'Profile not found' };
      }

      const profile = currentResult.data;
      const recommendations = profile.currentRecommendations;

      if (!recommendations) {
        return { success: false, error: 'No current recommendations to update' };
      }

      const existingInsights = recommendations.insightPlan?.insights || [];

      // Deduplicate by insightId across existing + new
      const seen = new Set<string>(existingInsights.map(i => i.insightId));
      const mergedInsights = [...existingInsights];
      newInsights.forEach(insight => {
        if (!seen.has(insight.insightId)) {
          seen.add(insight.insightId);
          mergedInsights.push(insight);
        }
      });

      const updatedRecommendations: DailyRecommendations = {
        ...recommendations,
        insightPlan: {
          title: recommendations.insightPlan?.title || fallbackTitle,
          insights: mergedInsights,
        },
      };

      const cleanedRecommendations = cleanUndefinedDeep(updatedRecommendations);
      const now = Date.now() / 1000;

      await this.updateDoc(userId, {
        currentRecommendations: cleanedRecommendations,
        updatedAtTimestamp: now,
      });

      return {
        success: true,
        data: {
          ...profile,
          currentRecommendations: updatedRecommendations,
          updatedAtTimestamp: now,
        },
      };
    } catch (error: any) {
      console.error('Error appending insights to plan:', error);
      return {
        success: false,
        error: error.message || 'Failed to append insights to plan',
      };
    }
  }

  /**
   * Update user's onboarding answers
   * Uses partial updates for better performance and security
   */
  static async updateOnboardingAnswers(
    userId: string,
    onboardingData: OnboardingAnswersCollection
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error };
      }

      const updatedProfile = updateOnboardingAnswers(
        currentResult.data,
        onboardingData
      );

      // Use updateDoc to only update the onboardingAnswers field
      const cleanedOnboardingAnswers = cleanUndefinedDeep(onboardingData);
      
      await this.updateDoc(userId, {
        onboardingAnswers: cleanedOnboardingAnswers,
        updatedAtTimestamp: Date.now() / 1000,
      });
      
      return { success: true, data: updatedProfile };
    } catch (error: any) {
      console.error('Error updating onboarding answers:', error);
      return {
        success: false,
        error: error.message || 'Failed to update onboarding answers',
      };
    }
  }

  /**
   * Update user's active context
   */
  static async updateActiveContext(
    userId: string,
    context: Partial<Omit<ActiveContext, 'lastUpdated' | 'source'>>,
    source: ActiveContext['source']
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error };
      }

      const updatedProfile = updateActiveContext(
        currentResult.data,
        context,
        source
      );

      // Use updateDoc to only update specific fields instead of entire document
      const cleanedActiveContext = cleanUndefinedDeep(updatedProfile.activeContext);
      
      await this.updateDoc(userId, {
        activeContext: cleanedActiveContext,
        updatedAtTimestamp: Date.now() / 1000, // Float instead of integer
      });
      
      return { success: true, data: updatedProfile };
    } catch (error: any) {
      console.error('Error updating user active context:', error);
      return {
        success: false,
        error: error.message || 'Failed to update user active context',
      };
    }
  }

  /**
   * Update user's core profile based on AI analysis
   */
  static async updateCoreProfile(
    userId: string,
    updates: Partial<Omit<CoreProfile, 'lastUpdated' | 'source'>>,
    source: string
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error };
      }

      const updatedProfile = updateCoreProfile(
        currentResult.data,
        updates,
        source
      );

      // Use updateDoc to only update specific fields instead of entire document
      const cleanedCoreProfile = cleanUndefinedDeep(updatedProfile.coreProfile);
      
      await this.updateDoc(userId, {
        coreProfile: cleanedCoreProfile,
        updatedAtTimestamp: Date.now() / 1000, // Float instead of integer
      });

      return { success: true, data: updatedProfile };
    } catch (error: any) {
      console.error('Error updating core profile:', error);
      return {
        success: false,
        error: error.message || 'Failed to update core profile',
      };
    }
  }

  /**
   * Update multiple onboarding-related fields at once
   * Used for importing data from external services
   */
  static async updateOnboardingData(
    userId: string,
    data: {
      onboardingAnswers?: OnboardingAnswersCollection;
      coreProfile?: Partial<CoreProfile>;
      activeContext?: Partial<ActiveContext>;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const now = Date.now() / 1000;

      const updates: any = {
        updatedAtTimestamp: now,
      };

      if (data.onboardingAnswers) {
        updates.onboardingAnswers = data.onboardingAnswers;
      }

      if (data.coreProfile) {
        updates.coreProfile = {
          ...data.coreProfile,
          lastUpdated: now,
        };
      }

      if (data.activeContext) {
        updates.activeContext = {
          ...data.activeContext,
          lastUpdated: now,
        };
      }

      const cleanedUpdates = cleanUndefinedDeep(updates);
      await this.updateDoc(userId, cleanedUpdates);

      return { success: true };
    } catch (error: any) {
      console.error('Error updating onboarding data:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Toggle book favorite status
   */
  static async toggleBookFavorite(
    userId: string,
    bookId: string,
    isFavorite: boolean
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    const updates: Partial<UserBookProgress> = {
      isFavorite,
      ...(isFavorite && { favoriteAddedAt: Date.now() / 1000 }),
    };

    return this.updateUserBookProgress(userId, bookId, updates);
  }

  /**
   * Mark a book as completed
   */
  static async markBookCompleted(
    userId: string,
    bookId: string
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    const updates: Partial<UserBookProgress> = {
      isCompleted: true,
      completedAt: Date.now() / 1000,
      completionPercentage: 100,
    };

    return this.updateUserBookProgress(userId, bookId, updates);
  }

  /**
   * Mark an insight as completed
   */
  static async markInsightCompleted(
    userId: string,
    insightId: string,
    bookId: string
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    const updates: Partial<UserInsightProgress> = {
      isCompleted: true,
      completedAt: Date.now() / 1000,
    };

    return this.updateUserInsightProgress(userId, insightId, bookId, updates);
  }

  /**
   * Update user's profile summary from AI
   * Uses partial updates for better performance and security
   */
  static async updateUserProfileSummary(
    userId: string,
    summary: ProfileSummary
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      console.log(`📝 Updating profile summary for user ${userId}`);
      
      // Get existing profile (should exist after initialization)
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error || 'Profile not found' };
      }

      // Update the profile summary
      const updatedProfile = updateProfileSummary(currentResult.data, summary);

      // Use updateDoc to only update the profileSummary field
      const profileSummaryWithTimestamp = {
        ...summary,
        lastUpdated: Date.now() / 1000, // Ensure required field is included
      };
      const cleanedSummary = cleanUndefinedDeep(profileSummaryWithTimestamp);
      
      await this.updateDoc(userId, {
        profileSummary: cleanedSummary,
        updatedAtTimestamp: Date.now() / 1000,
      });
      
      console.log(`✅ Profile summary updated successfully for user ${userId}`);
      return { success: true, data: updatedProfile };
    } catch (error: any) {
      console.error('Error updating profile summary:', error);
      return {
        success: false,
        error: error.message || 'Failed to update profile summary',
      };
    }
  }

  /**
   * Update daily recommendations from AI
   * Also saves recommendation reasons to individual book/insight progress
   */
  static async updateUserDailyRecommendations(
    userId: string,
    recommendations: DailyRecommendations
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      console.log(`📚 Updating daily recommendations for user ${userId}`);
      
      // Get existing profile (should exist after initialization)
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error || 'Profile not found' };
      }

      // Ensure all required fields are present with proper defaults
      const now = Date.now() / 1000;
      const today = new Date().toISOString().split('T')[0];
      
      const validatedRecommendations: DailyRecommendations = {
        date: recommendations.date || today,
        personalizedMessage: recommendations.personalizedMessage || 'Here are your personalized recommendations for today.',
        recommendedBooks: (recommendations.recommendedBooks || []).map(book => ({
          ...book,
          bookId: normalizeBookId(book.bookId) || '',
          recommendedAt: book.recommendedAt || now,
        })),
        insightPlan: recommendations.insightPlan ? {
          title: recommendations.insightPlan.title,
          insights: (recommendations.insightPlan.insights || []).map(insight => ({
            ...insight,
            bookId: insight.bookId ? normalizeBookId(insight.bookId) || undefined : undefined,
            recommendedAt: insight.recommendedAt || now,
          })),
        } : undefined,
        query: recommendations.query,
        generatedAt: recommendations.generatedAt || now,
      };

      // Filter out any book recommendations that failed normalization
      validatedRecommendations.recommendedBooks = validatedRecommendations.recommendedBooks.filter(book => !!book.bookId);

      let updatedProfile = updateDailyRecommendations(currentResult.data, validatedRecommendations);

      // Save recommendation reasons to individual progress tracking
      updatedProfile = await this.saveRecommendationReasons(updatedProfile, validatedRecommendations);

      // Use updateDoc to update all fields at once
      const cleanedRecommendations = cleanUndefinedDeep(validatedRecommendations);
      const cleanedBooksProgress = cleanUndefinedDeep(updatedProfile.booksProgress);
      const cleanedInsightsProgress = cleanUndefinedDeep(updatedProfile.insightsProgress);
      
      try {
        await this.updateDoc(userId, {
          currentRecommendations: cleanedRecommendations,
          booksProgress: cleanedBooksProgress,
          insightsProgress: cleanedInsightsProgress,
          updatedAtTimestamp: now,
        });
      } catch (updateError: any) {
        console.error('❌ Failed to save daily recommendations to Firestore:', updateError);
        
        // Report detailed Firestore error to Sentry
        const { errorReporting } = require('./errorReportingService');
        errorReporting.reportError(updateError, {
          userId,
          screen: 'PrivateProfileService',
          action: 'updateUserDailyRecommendations',
          metadata: {
            errorCode: updateError?.code,
            errorMessage: updateError?.message,
            // Firestore specific details
            isPermissionDenied: updateError?.code === 'permission-denied' || updateError?.message?.includes('permission-denied'),
            isAnonymous: auth.currentUser?.isAnonymous,
            userAuthenticated: !!auth.currentUser,
            // Data being saved
            booksCount: cleanedRecommendations.recommendedBooks?.length || 0,
            insightsCount: cleanedRecommendations.insightPlan?.insights?.length || 0,
            hasCurrentRecommendations: !!currentResult.data.currentRecommendations,
            isCritical: true,
          },
        });
        
        throw updateError; // Re-throw so caller gets the detailed error
      }
      
      return { success: true, data: updatedProfile };
    } catch (error: any) {
      console.error('Error updating daily recommendations:', error);
      return {
        success: false,
        error: error.message || 'Failed to update daily recommendations',
      };
    }
  }

  /**
   * Save recommendation reasons to individual book/insight progress
   * This ensures the AI recommendation text is always available regardless of navigation
   */
  private static async saveRecommendationReasons(
    profile: PrivateProfile,
    recommendations: DailyRecommendations
  ): Promise<PrivateProfile> {
    let updatedProfile = { ...profile };
    const now = Date.now() / 1000;

    // Save book recommendation reasons
    for (const bookRec of recommendations.recommendedBooks) {
      if (bookRec.reason) {
        const bookId = normalizeBookId(bookRec.bookId);
        if (!bookId) {
          console.warn('[PrivateProfileService] Skipping book reason save due to invalid bookId:', bookRec.bookId);
          continue;
        }
        
        // Add book progress if it doesn't exist
        if (!updatedProfile.booksProgress[bookId]) {
          updatedProfile = addBookProgress(updatedProfile, bookId);
        }
        
        // Update with recommendation reason
        updatedProfile = updateBookProgress(updatedProfile, bookId, {
          recommendationReason: bookRec.reason,
          recommendedAt: bookRec.recommendedAt || now,
        });
      }
    }

    // Save insight recommendation reasons
    if (recommendations.insightPlan?.insights) {
      for (const insightRec of recommendations.insightPlan.insights) {
        if (insightRec.reason) {
          const insightId = insightRec.insightId;
          const bookId = insightRec.bookId || 'unknown';
          
          // Create insight progress if it doesn't exist
          if (!updatedProfile.insightsProgress[insightId]) {
            const insightProgress: UserInsightProgress = {
              id: insightId,
              bookId: bookId,
              startedAt: now,
              lastAccessedAt: now,
              isCompleted: false,
              recommendationReason: insightRec.reason,
              recommendedAt: insightRec.recommendedAt || now,
            };
            
            updatedProfile = {
              ...updatedProfile,
              insightsProgress: {
                ...updatedProfile.insightsProgress,
                [insightId]: insightProgress,
              },
            };
          } else {
            // Update existing insight progress with recommendation reason
            const existingProgress = updatedProfile.insightsProgress[insightId];
            updatedProfile = {
              ...updatedProfile,
              insightsProgress: {
                ...updatedProfile.insightsProgress,
                [insightId]: {
                  ...existingProgress,
                  recommendationReason: insightRec.reason,
                  recommendedAt: insightRec.recommendedAt || now,
                  lastAccessedAt: now,
                },
              },
            };
          }
        }
      }
    }

    return updatedProfile;
  }

  /**
   * Update the recommendation reason for a specific book
   * Used when generating personalized messages on-demand
   */
  static async updateBookRecommendationReason(
    userId: string,
    bookId: string,
    reason: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const normalizedBookId = normalizeBookId(bookId);
      if (!normalizedBookId) {
        return { success: false, error: 'Invalid bookId' };
      }

      // Get current profile
      const profileResult = await this.getProfile(userId);
      if (!profileResult.success || !profileResult.data) {
        return { success: false, error: 'Profile not found' };
      }

      const profile = profileResult.data;

      // Update the reason in currentRecommendations
      if (profile.currentRecommendations?.recommendedBooks) {
        const bookIndex = profile.currentRecommendations.recommendedBooks.findIndex(
          b => normalizeBookId(b.bookId) === normalizedBookId
        );
        
        if (bookIndex !== -1) {
          profile.currentRecommendations.recommendedBooks[bookIndex].bookId = normalizedBookId;
          profile.currentRecommendations.recommendedBooks[bookIndex].reason = reason;
          
          // Save to Firestore
          await this.updateProfile(userId, {
            currentRecommendations: profile.currentRecommendations,
          });
          
          console.log(`[PrivateProfileService] Updated recommendation reason for book: ${normalizedBookId}`);
          return { success: true };
        }
      }

      return { success: false, error: 'Book not found in recommendations' };
    } catch (error) {
      console.error('[PrivateProfileService] Error updating book recommendation reason:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Add a chat history entry
   * Also saves recommendation reasons to individual book/insight progress
   */
  static async addUserChatHistory(
    userId: string,
    chatEntry: MentorChatHistory
  ): Promise<{ success: boolean; data?: PrivateProfile; error?: string }> {
    try {
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error };
      }

      // Ensure all required fields are present with proper defaults
      const now = Date.now() / 1000;
      
      const validatedChatEntry: MentorChatHistory = {
        ...chatEntry,
        timestamp: chatEntry.timestamp || now,
        // Ensure recommendations have all required fields
        ...(chatEntry.recommendations && {
          recommendations: {
            ...chatEntry.recommendations,
            // Ensure books have recommendedAt field
            books: (chatEntry.recommendations.books || []).map(book => ({
              ...book,
              bookId: normalizeBookId(book.bookId) || '',
              recommendedAt: book.recommendedAt || now,
            })),
            // Ensure insights have recommendedAt field
            insights: (chatEntry.recommendations.insights || []).map(insight => ({
              ...insight,
              bookId: insight.bookId ? normalizeBookId(insight.bookId) || undefined : undefined,
              recommendedAt: insight.recommendedAt || now,
            })),
          },
        }),
      };

      // Filter out invalid books after normalization
      if (validatedChatEntry.recommendations?.books) {
        validatedChatEntry.recommendations.books = validatedChatEntry.recommendations.books.filter(book => !!book.bookId);
      }

      let updatedProfile = addChatHistory(currentResult.data, validatedChatEntry);

      // Save recommendation reasons to individual progress tracking
      if (validatedChatEntry.recommendations) {
        updatedProfile = await this.saveChatRecommendationReasons(updatedProfile, validatedChatEntry.recommendations);
      }

      // Update dashboard recommendations if chat has new recommendations
      if (validatedChatEntry.recommendations) {
        updatedProfile = this.updateDashboardRecommendations(updatedProfile, validatedChatEntry.recommendations);
      }

      // Use updateDoc to update all fields at once
      const cleanedChatHistory = cleanUndefinedDeep(updatedProfile.chatHistory);
      const cleanedBooksProgress = cleanUndefinedDeep(updatedProfile.booksProgress);
      const cleanedInsightsProgress = cleanUndefinedDeep(updatedProfile.insightsProgress);
      const cleanedCurrentRecommendations = cleanUndefinedDeep(updatedProfile.currentRecommendations);
      
      await this.updateDoc(userId, {
        chatHistory: cleanedChatHistory,
        booksProgress: cleanedBooksProgress,
        insightsProgress: cleanedInsightsProgress,
        currentRecommendations: cleanedCurrentRecommendations,
        updatedAtTimestamp: now,
      });
      
      return { success: true, data: updatedProfile };
    } catch (error: any) {
      console.error('Error adding chat history:', error);
      return {
        success: false,
        error: error.message || 'Failed to add chat history',
      };
    }
  }

  /**
   * Save recommendation reasons from chat to individual book/insight progress
   */
  private static async saveChatRecommendationReasons(
    profile: PrivateProfile,
    recommendations: {
      books?: AIBookRecommendation[];
      insights?: AIInsightRecommendation[];
    }
  ): Promise<PrivateProfile> {
    let updatedProfile = { ...profile };
    const now = Date.now() / 1000;

    // Save book recommendation reasons from chat
    if (recommendations.books) {
      for (const bookRec of recommendations.books) {
        if (bookRec.reason) {
          const bookId = bookRec.bookId.includes('_') ? bookRec.bookId.split('_')[0] : bookRec.bookId;
          
          // Add book progress if it doesn't exist
          if (!updatedProfile.booksProgress[bookId]) {
            updatedProfile = addBookProgress(updatedProfile, bookId);
          }
          
          // Update with recommendation reason
          updatedProfile = updateBookProgress(updatedProfile, bookId, {
            recommendationReason: bookRec.reason,
            recommendedAt: bookRec.recommendedAt || now,
          });
        }
      }
    }

    // Save insight recommendation reasons from chat
    if (recommendations.insights) {
      for (const insightRec of recommendations.insights) {
        if (insightRec.reason) {
          const insightId = insightRec.insightId;
          const bookId = insightRec.bookId || 'unknown';
          
          // Create insight progress if it doesn't exist
          if (!updatedProfile.insightsProgress[insightId]) {
            const insightProgress: UserInsightProgress = {
              id: insightId,
              bookId: bookId,
              startedAt: now,
              lastAccessedAt: now,
              isCompleted: false,
              recommendationReason: insightRec.reason,
              recommendedAt: insightRec.recommendedAt || now,
            };
            
            updatedProfile = {
              ...updatedProfile,
              insightsProgress: {
                ...updatedProfile.insightsProgress,
                [insightId]: insightProgress,
              },
            };
          } else {
            // Update existing insight progress with recommendation reason
            const existingProgress = updatedProfile.insightsProgress[insightId];
            updatedProfile = {
              ...updatedProfile,
              insightsProgress: {
                ...updatedProfile.insightsProgress,
                [insightId]: {
                  ...existingProgress,
                  recommendationReason: insightRec.reason,
                  recommendedAt: insightRec.recommendedAt || now,
                  lastAccessedAt: now,
                },
              },
            };
          }
        }
      }
    }

    return updatedProfile;
  }

  /**
   * Update dashboard recommendations with new chat recommendations
   * Adds new items to the top and keeps only top 10 books and 5 insights
   */
  private static updateDashboardRecommendations(
    profile: PrivateProfile,
    newRecommendations: {
      books?: AIBookRecommendation[];
      insights?: AIInsightRecommendation[];
    }
  ): PrivateProfile {
    if (!newRecommendations.books?.length && !newRecommendations.insights?.length) {
      return profile;
    }

    const now = Date.now() / 1000;
    const currentRecs = profile.currentRecommendations;
    
    // Start with existing recommendations or create new structure
    let existingBooks: AIBookRecommendation[] = (currentRecs?.recommendedBooks || []).map(book => ({
      ...book,
      bookId: normalizeBookId(book.bookId) || '',
    })).filter(book => !!book.bookId);
    let existingInsights: AIInsightRecommendation[] = currentRecs?.insightPlan?.insights || [];
    
    // Add new books to the top of the list
    if (newRecommendations.books?.length) {
      console.log(`📚 Adding ${newRecommendations.books.length} new books to dashboard`);
      
      // Remove duplicates by bookId, prioritizing new recommendations
      const normalizedNewBooks = newRecommendations.books.map(book => ({
        ...book,
        bookId: normalizeBookId(book.bookId) || '',
      })).filter(book => !!book.bookId);

      const existingBookIds = new Set(existingBooks.map(book => book.bookId));
      const newUniqueBooks = normalizedNewBooks.filter(book => !existingBookIds.has(book.bookId));
      
      // Combine new books at the top, then existing books
      const allBooks = [...newUniqueBooks, ...existingBooks];
      
      // Keep only top 10 books
      existingBooks = allBooks.slice(0, 10);
      
      console.log(`📊 Dashboard books: ${newUniqueBooks.length} new + ${existingBooks.length - newUniqueBooks.length} existing = ${existingBooks.length} total`);
    }
    
    // Add new insights to the top of the list
    if (newRecommendations.insights?.length) {
      console.log(`💡 Adding ${newRecommendations.insights.length} new insights to dashboard`);
      
      // Remove duplicates by insightId, prioritizing new recommendations
      const existingInsightIds = new Set(existingInsights.map(insight => insight.insightId));
      const newUniqueInsights = newRecommendations.insights.filter(insight => !existingInsightIds.has(insight.insightId));
      
      // Combine new insights at the top, then existing insights
      const allInsights = [...newUniqueInsights, ...existingInsights];
      
      // Keep only top 5 insights
      existingInsights = allInsights.slice(0, 5);
      
      console.log(`📊 Dashboard insights: ${newUniqueInsights.length} new + ${existingInsights.length - newUniqueInsights.length} existing = ${existingInsights.length} total`);
    }
    
    // Create updated recommendations structure
    const updatedRecommendations: DailyRecommendations = {
      date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
      personalizedMessage: currentRecs?.personalizedMessage || 'New recommendations from your chat',
      recommendedBooks: existingBooks,
      insightPlan: existingInsights.length > 0 ? {
        title: currentRecs?.insightPlan?.title || 'Insights for Your Growth',
        insights: existingInsights,
      } : undefined,
      query: currentRecs?.query || 'Chat recommendations',
      generatedAt: now,
    };
    
    // Use the existing updateDailyRecommendations function
    return updateDailyRecommendations(profile, updatedRecommendations);
  }

  /**
   * Get user's current recommendations
   */
  static async getCurrentRecommendations(
    userId: string
  ): Promise<{ success: boolean; data?: DailyRecommendations; error?: string }> {
    try {
      const profileResult = await this.getProfile(userId);
      if (!profileResult.success || !profileResult.data) {
        return { success: false, error: profileResult.error };
      }

      const today = new Date().toISOString().split('T')[0];
      const recommendations = profileResult.data.currentRecommendations;

      // Check if recommendations are for today
      if (recommendations && recommendations.date === today) {
        return { success: true, data: recommendations };
      }

      return { success: false, error: 'No recommendations for today' };
    } catch (error: any) {
      console.error('Error getting current recommendations:', error);
      return {
        success: false,
        error: error.message || 'Failed to get current recommendations',
      };
    }
  }

  /**
   * Update device info in user profile
   * Should be called once on app start to save comprehensive device info to profile
   * This prevents sending device info with every log event
   * 
   * Stores device info in userData map to avoid breaking existing deviceInfo string field
   */
  static async updateUserDeviceInfo(
    userId: string,
    appVersion: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('📱 Updating device info for user:', userId);
      
      // Get comprehensive device info
      const deviceInfoObj = getComprehensiveDeviceInfo();
      
      // Get current profile
      const currentResult = await this.getProfile(userId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: currentResult.error };
      }

      const profile = currentResult.data;
      
      // Store comprehensive device info in userData map (already exists, no breaking changes)
      const updatedUserData = {
        ...profile.userData,
        // Store device info with 'device_' prefix to avoid conflicts
        device_platform: deviceInfoObj.platform,
        device_osName: deviceInfoObj.osName || '',
        device_osVersion: deviceInfoObj.osVersion || '',
        device_model: deviceInfoObj.deviceModel || '',
        device_brand: deviceInfoObj.deviceBrand || '',
        device_type: deviceInfoObj.deviceType || '',
        device_screenWidth: String(deviceInfoObj.screenWidth || ''),
        device_screenHeight: String(deviceInfoObj.screenHeight || ''),
        device_pixelRatio: String(deviceInfoObj.pixelRatio || ''),
        device_lastUpdated: String(Date.now()),
      };

      // Prepare update object
      const updateData: any = {
        userData: cleanUndefinedDeep(updatedUserData),
        updatedAtTimestamp: Date.now() / 1000,
      };

      // Also update countryCode at root level if available
      // Note: This is from locale (not perfect), but logs get accurate one from backend (IP-based)
      if (deviceInfoObj.countryCode) {
        updateData.countryCode = deviceInfoObj.countryCode;
      }

      // Save to Firestore
      await this.updateDoc(userId, updateData);
      
      console.log('✅ Device info stored in userData');
      return { success: true };
    } catch (error: any) {
      console.error('Error updating device info:', error);
      return {
        success: false,
        error: error.message || 'Failed to update device info',
      };
    }
  }

  /**
   * Call this immediately after authentication to ensure user has a profile
   * Creates profile if it doesn't exist (ensureProfileExists handles the existence check internally)
   */
  static async initializeUserProfile(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🔥 Initializing user profile after authentication:', userId);
      
      // Small delay to ensure authentication is fully complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check current user auth state
      const currentUser = auth.currentUser;
      if (!currentUser || currentUser.uid !== userId) {
        return {
          success: false,
          error: 'User not authenticated or ID mismatch',
        };
      }

      // Create/ensure profile exists (this method handles the existence check internally)
      // No need to check first - ensureProfileExists is idempotent
      const result = await this.ensureProfileExists(userId, {
        email: currentUser.email || undefined,
        displayName: currentUser.displayName || undefined,
      });

      if (result.success) {
        console.log('✅ User profile initialized successfully');
        
        // Update device info once on profile initialization (non-blocking, best effort)
        // Don't let this fail the profile initialization
        const appVersion = Constants.expoConfig?.version || '1.0.0';
        this.updateUserDeviceInfo(userId, appVersion).catch(error => {
          console.warn('⚠️ Device info update failed (non-critical):', error.message);
          // Device info update is nice-to-have but not critical for app functionality
        });
        
        return { success: true };
      } else {
        console.error('❌ Failed to initialize user profile:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error: any) {
      console.error('❌ Error initializing user profile:', error);
      return {
        success: false,
        error: error.message || 'Failed to initialize user profile',
      };
    }
  }

  /**
   * Get user's main focus category and label
   */
  static async getMainFocus(
    userId: string
  ): Promise<{ success: boolean; data?: { category: string; label: string; primaryGoal?: string }; error?: string }> {
    try {
      const profileResult = await this.getProfile(userId);
      if (!profileResult.success || !profileResult.data) {
        return { success: false, error: profileResult.error };
      }

      const profile = profileResult.data;
      const coreProfile = profile.coreProfile;

      if (coreProfile?.mainFocusCategory && coreProfile?.mainFocusLabel) {
        return {
          success: true,
          data: {
            category: coreProfile.mainFocusCategory,
            label: coreProfile.mainFocusLabel,
            primaryGoal: coreProfile.primaryGoalText,
          },
        };
      }

      return { success: false, error: 'No main focus set' };
    } catch (error: any) {
      console.error('Error getting main focus:', error);
      return {
        success: false,
        error: error.message || 'Failed to get main focus',
      };
    }
  }

  /**
   * Update user's main focus category and label
   */
  static async updateMainFocus(
    userId: string,
    category: string,
    label: string,
    primaryGoal?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`🎯 Updating main focus for user ${userId}:`, category, label);

      const result = await this.updateCoreProfile(
        userId,
        {
          mainFocusCategory: category as any,
          mainFocusLabel: label,
          primaryGoalText: primaryGoal,
        },
        'focus_change'
      );

      if (result.success) {
        console.log('✅ Main focus updated successfully');
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error: any) {
      console.error('Error updating main focus:', error);
      return {
        success: false,
        error: error.message || 'Failed to update main focus',
      };
    }
  }

  // ============================================================================
  // CACHE UTILITIES
  // ============================================================================

  /**
   * Get profile from memory cache
   */
  private static getFromMemoryCache(userId: string): PrivateProfile | null {
    const cacheKey = `${PROFILE_CACHE_PREFIX}${userId}`;
    const cached = this.memoryCache.get(cacheKey);
    if (!cached) return null;

    const age = Date.now() - cached.timestamp;
    if (age > PROFILE_CACHE_TTL) {
      this.memoryCache.delete(cacheKey);
      return null;
    }

    return cached.data;
  }

  /**
   * Set profile in memory cache
   */
  private static setMemoryCache(userId: string, data: PrivateProfile): void {
    const cacheKey = `${PROFILE_CACHE_PREFIX}${userId}`;
    this.memoryCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });

    // Limit memory cache size to 10 profiles
    if (this.memoryCache.size > 10) {
      const firstKey = this.memoryCache.keys().next().value;
      if (firstKey) {
        this.memoryCache.delete(firstKey);
      }
    }
  }

  /**
   * Get profile from AsyncStorage cache
   */
  private static async getFromCache(userId: string): Promise<PrivateProfile | null> {
    try {
      const cacheKey = `${PROFILE_CACHE_PREFIX}${userId}`;
      const cached = await AsyncStorage.getItem(cacheKey);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      if (age > PROFILE_CACHE_TTL) {
        await AsyncStorage.removeItem(cacheKey);
        return null;
      }

      // Validate with schema before returning
      return PrivateProfileSchema.parse(data);
    } catch (error) {
      console.error('[ProfileCache] Error reading cache:', error);
      return null;
    }
  }

  /**
   * Set profile in AsyncStorage cache
   */
  private static async setCache(userId: string, data: PrivateProfile): Promise<void> {
    try {
      const cacheKey = `${PROFILE_CACHE_PREFIX}${userId}`;
      const cacheData = {
        data,
        timestamp: Date.now(),
      };
      await AsyncStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('[ProfileCache] Error writing cache:', error);
      // Don't throw - caching is optional, continue without cache
    }
  }

  /**
   * Invalidate all caches for a user
   */
  static async invalidateProfileCache(userId: string): Promise<void> {
    const cacheKey = `${PROFILE_CACHE_PREFIX}${userId}`;
    
    // Clear memory cache
    this.memoryCache.delete(cacheKey);
    
    // Clear AsyncStorage cache
    try {
      await AsyncStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('[ProfileCache] Error invalidating cache:', error);
    }
  }
} 